import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { notification, Spin } from 'antd';
import { Icon, Select, Button } from 'Components';
import LMSButton from '../components/Button';
import styles from 'LearningManager/styles/CourseAssignments.module.scss';
import { Assignment } from 'LearningManager/constants/CoursesAssignment.types';
import {
	CourseData,
	LearningPathData,
	LearningPathGroup
} from 'LearningManager/constants/UserDashboard.constants';
import LearningPathCard from 'LearningManager/components/LearningPathCard';
import CourseCard from '../components/CourseCard';
import FilterSearch from '../components/FilterSearch';
import ToggleGroup from 'LearningManager/components/ToggleGroup';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import UnassignTrainingModal from 'LearningManager/components/UnassignTrainingModal';
import UnassignScheduledModal, {
	UnassignTarget
} from 'LearningManager/components/UnassignScheduledModal';
import ViewDetailsDrawer from 'LearningManager/components/ViewDetailsDrawer';
import CourseDetailsDrawer from 'LearningManager/components/CourseDetailsDrawer';
import ResetCompletionModal, {
	ResetTarget
} from 'LearningManager/components/ResetCompletionModal';
import BulkActionsBar from 'LearningManager/components/BulkActionsBar';
import BulkResetModal from 'LearningManager/components/BulkResetModal';
import { StepperProvider } from 'LearningManager/hooks/Stepper.context';
import HeaderRow from 'Pages/TenantProvider/components/HeaderRow';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import { AssignmentSource } from 'Services/Api/AssignmentSources/Types';
import { LearningPath } from 'Services/Api/LearningManager/Types';
import { Course } from 'Services/Api/Courses/Types';

const { Option } = Select;

interface CourseItem {
	id: number;
	title: string;
	image: string;
	tags: string[];
	status?: 'Completed' | 'In Progress' | 'Not Started';
}

const MOCK_COURSES: CourseItem[] = [
	{
		id: 1,
		title: '[Course name]',
		image: course1,
		tags: ['Required'],
		status: 'Completed'
	},
	{
		id: 2,
		title: '[Course name]',
		image: course2,
		tags: ['Optional', 'Required'],
		status: 'In Progress'
	},
	{
		id: 3,
		title: '[Course name]',
		image: course3,
		tags: ['Required'],
		status: 'Completed'
	},
	{
		id: 4,
		title: '[Course name]',
		image: course4,
		tags: ['Optional'],
		status: 'Not Started'
	},
	{
		id: 5,
		title: '[Course name]',
		image: course1,
		tags: ['Required'],
		status: 'In Progress'
	},
	{
		id: 6,
		title: '[Course name]',
		image: course2,
		tags: ['Required'],
		status: 'Completed'
	},
	{
		id: 7,
		title: '[Course name]',
		image: course3,
		tags: ['Optional', 'Scheduled'],
		status: 'Not Started'
	},
	{
		id: 8,
		title: '[Course name]',
		image: course4,
		tags: ['Required'],
		status: 'In Progress'
	},
	{
		id: 9,
		title: '[Course name]',
		image: course1,
		tags: ['Optional'],
		status: 'Completed'
	},
	{
		id: 10,
		title: '[Course name]',
		image: course2,
		tags: ['Required'],
		status: 'Not Started'
	},
	{
		id: 11,
		title: '[Course name]',
		image: course3,
		tags: ['Scheduled'],
		status: 'In Progress'
	},
	{
		id: 12,
		title: '[Course name]',
		image: course4,
		tags: ['Required'],
		status: 'Completed'
	},
	{
		id: 13,
		title: '[Course name]',
		image: course1,
		tags: ['Optional'],
		status: 'Not Started'
	},
	{
		id: 14,
		title: '[Course name]',
		image: course2,
		tags: ['Required'],
		status: 'In Progress'
	},
	{
		id: 15,
		title: '[Course name]',
		image: course3,
		tags: ['Required', 'Scheduled'],
		status: 'Completed'
	},
	{
		id: 16,
		title: '[Course name]',
		image: course4,
		tags: ['Optional'],
		status: 'Not Started'
	},
	{
		id: 17,
		title: '[Course name]',
		image: course1,
		tags: ['Required'],
		status: 'In Progress'
	},
	{
		id: 18,
		title: '[Course name]',
		image: course2,
		tags: ['Optional'],
		status: 'Completed'
	},
	{
		id: 19,
		title: '[Course name]',
		image: course3,
		tags: ['Required'],
		status: 'Not Started'
	},
	{
		id: 20,
		title: '[Course name]',
		image: course4,
		tags: ['Scheduled'],
		status: 'In Progress'
	}
];

const PLACEHOLDER_IMAGES = [course1, course2, course3, course4];

const buildCourseData = (
	courseId: number,
	isRequired: boolean,
	coursesById: Record<number, Course>,
	imageIndex: number
): CourseData => {
	const course = coursesById[courseId];
	return {
		id: courseId,
		title: course?.name ?? `Course ${courseId}`,
		meta: '',
		tags: [isRequired ? 'Required' : 'Optional'],
		image: PLACEHOLDER_IMAGES[imageIndex % PLACEHOLDER_IMAGES.length]
	};
};

const toAssignment = (
	source: AssignmentSource,
	learningPathsById: Record<number, LearningPath>,
	coursesById: Record<number, Course>
): Assignment => {
	const learningPathGroups: LearningPathGroup[] = source.learning_paths.map(
		(lpRef) => {
			const lp = learningPathsById[lpRef.learning_path_id];
			const courses = (lp?.activities ?? []).map((c, idx) =>
				buildCourseData(c.activity_id, c.is_required, coursesById, idx)
			);
			return {
				id: `lp-${lpRef.learning_path_id}`,
				label: lp?.name ?? `Learning Path ${lpRef.learning_path_id}`,
				courses
			};
		}
	);

	const individualCourses: CourseData[] = source.activities.map(
		(activity, idx) =>
			buildCourseData(
				activity.activity_id,
				activity.is_required,
				coursesById,
				idx
			)
	);

	const courseCount =
		learningPathGroups.reduce((sum, g) => sum + g.courses.length, 0) +
		individualCourses.length;

	const isScheduled = moment(source.start_date).isAfter(moment(), 'day');

	return {
		id: String(source.id),
		name: source.name,
		type: source.is_assigned_to_future_users ? 'Ongoing' : 'One-Time',
		setupType: source.activities.length > 0 ? 'Custom Setup' : 'Quick Setup',
		status: isScheduled ? 'Scheduled' : 'Active',
		courseCount,
		dueDate: source.due_date ?? undefined,
		startsDate: source.start_date,
		learningPaths: learningPathGroups.length ? learningPathGroups : undefined,
		individualCourses: individualCourses.length
			? individualCourses
			: undefined
	};
};

const toLearningPathData = (assignment: Assignment): LearningPathData => {
	const completionPercentage = assignment.completionPercentage ?? 35;
	const completedCount =
		assignment.completedCount ??
		Math.round((completionPercentage / 100) * assignment.courseCount);

	return {
		id: assignment.id,
		title: assignment.name,
		statuses: [
			assignment.type,
			...(assignment.status === 'Scheduled' ? ['Scheduled'] : [])
		],
		courseCount: assignment.courseCount,
		completedCount,
		completionPercentage,
		dueDate: assignment.dueDate,
		startsDate: assignment.startsDate,
		courses: assignment.courses ?? [],
		learningPaths: assignment.learningPaths,
		individualCourses: assignment.individualCourses
	};
};

const CourseAssignments: React.FC = observer(() => {
	const { learningManagerStore } = useStores();
	const {
		assignmentSources,
		learningPathsById,
		coursesById,
		status
	} = learningManagerStore;

	const [activeTab, setActiveTab] = useState<string>('1');
	const [selectedOrg, setSelectedOrg] = useState<string>('all');
	const [filterSearch, setFilterSearch] = useState<string>('');
	const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
	const [checkedCourseIds, setCheckedCourseIds] = useState<Set<number>>(
		new Set()
	);
	const [assignments, setAssignments] = useState<Assignment[]>([]);
	const [unassignTarget, setUnassignTarget] = useState<Assignment | null>(
		null
	);
	const [detailTarget, setDetailTarget] = useState<Assignment | null>(null);
	const [
		courseDetailTarget,
		setCourseDetailTarget
	] = useState<CourseItem | null>(null);
	const [resetTarget, setResetTarget] = useState<Assignment | null>(null);
	const [
		courseResetTarget,
		setCourseResetTarget
	] = useState<CourseItem | null>(null);
	const [
		courseUnassignTarget,
		setCourseUnassignTarget
	] = useState<CourseItem | null>(null);
	const [showBulkReset, setShowBulkReset] = useState(false);
	const [showBulkUnassign, setShowBulkUnassign] = useState(false);
	const history = useHistory();
	const { url } = useRouteMatch();

	const scopesForAssignment = (assignmentId?: string): string[] => {
		if (!assignmentId) return [];
		const source = assignmentSources.find(
			(s) => String(s.id) === assignmentId
		);
		return source?.scopes ?? [];
	};

	useEffect(() => {
		learningManagerStore.listAssignmentSources({ offset: 0 });
	}, []);

	useEffect(() => {
		const learningPathIds = [
			...new Set(
				assignmentSources.flatMap((s) =>
					s.learning_paths.map((lp) => lp.learning_path_id)
				)
			)
		];
		learningPathIds
			.filter((id) => !learningPathsById[id])
			.forEach((id) => learningManagerStore.fetchLearningPathById(id));
	}, [assignmentSources]);

	useEffect(() => {
		// Single bulk load of the activity catalog into coursesById — replaces
		// the previous one-request-per-course fetch. Referenced courses (from
		// assignment activities and learning-path activities) are all covered.
		learningManagerStore.fetchAllCourses();
	}, []);

	const computedAssignments = useMemo(
		() =>
			assignmentSources.map((source) =>
				toAssignment(source, learningPathsById, coursesById)
			),
		[assignmentSources, learningPathsById, coursesById]
	);

	useEffect(() => {
		setAssignments(computedAssignments);
	}, [computedAssignments]);

	const isLoadingAssignments = status.listAssignmentSources === Status.Pending;

	const isSelectionActive = checkedIds.size > 0;

	const handleSelectAll = () => {
		setCheckedIds(new Set(assignments.map((a) => a.id)));
	};

	const handleDeselectAll = () => {
		setCheckedIds(new Set());
	};

	const handleBulkReset = () => {
		setShowBulkReset(true);
	};

	const notify = (message: string) => {
		notification.open({
			message,
			placement: 'topRight',
			duration: 3,
			className: 'lms-notification',
			closeIcon: <></>,
			icon: <Icon type="infoFilled" role="img" />,
			description: null
		});
	};

	const handleBulkResetConfirm = async () => {
		setShowBulkReset(false);
		const ids = Array.from(checkedIds);
		const failedIds = new Set<string>();

		// Serialized: one reset API call at a time
		for (const id of ids) {
			try {
				await learningManagerStore.resetAssignmentSource(Number(id));
			} catch {
				failedIds.add(id);
			}
		}

		setCheckedIds(failedIds);
		notify(
			failedIds.size
				? 'Some trainings could not be reset'
				: 'Training reset'
		);
	};

	const handleBulkUnassign = () => {
		setShowBulkUnassign(true);
	};

	const handleBulkUnassignConfirm = async () => {
		setShowBulkUnassign(false);
		const ids = Array.from(checkedIds);
		const failedIds = new Set<string>();

		// Serialized: one unassign (delete) API call at a time
		for (const id of ids) {
			try {
				await learningManagerStore.deleteAssignmentSource(Number(id));
				setAssignments((prev) => prev.filter((a) => a.id !== id));
			} catch {
				failedIds.add(id);
			}
		}

		setCheckedIds(failedIds);
		notify(
			failedIds.size
				? 'Some trainings could not be unassigned'
				: 'Training unassigned'
		);
	};
	const { search } = useLocation();
	const params = new URLSearchParams(search);
	// Show the empty state once the fetch has completed and the org genuinely has
	// no assignments. The URL param stays as a manual override for previewing.
	const hasNoAssignments =
		status.listAssignmentSources === Status.Success &&
		assignments.length === 0;
	const showNoAssignments =
		params.has('showNoAssignments') || hasNoAssignments;
	const showNoFilterMatches = params.has('showNoFilterMatches');

	const handleCheck = (id: string, checked: boolean) => {
		setCheckedIds((prev) => {
			const next = new Set(prev);
			checked ? next.add(id) : next.delete(id);
			return next;
		});
	};

	const handleMenuAction = (id: string, action: string) => {
		const assignment = assignments.find((item) => item.id === id);

		if (action === 'edit') {
			const setupType = assignment?.setupType ?? 'Quick Setup';
			const params = new URLSearchParams({
				setupType,
				assignmentId: id
			});
			if (assignment?.status === 'Scheduled') {
				params.set('isScheduled', 'true');
			}

			history.push(`${url}/create-assignment?${params.toString()}`);
			return;
		}

		if (action === 'view-details' && assignment) {
			setDetailTarget(assignment);
			return;
		}

		if (action === 'unassign' && assignment) {
			setUnassignTarget(assignment);
			return;
		}

		if (action === 'reset-completion' && assignment) {
			setResetTarget(assignment);
			return;
		}

		console.log('Menu action:', action, 'on assignment:', id);
	};

	const handleConfirmUnassign = async () => {
		if (!unassignTarget) return;

		try {
			await learningManagerStore.deleteAssignmentSource(
				Number(unassignTarget.id)
			);
			setAssignments((prev) =>
				prev.filter((a) => a.id !== unassignTarget.id)
			);
			setCheckedIds((prev) => {
				const next = new Set(prev);
				next.delete(unassignTarget.id);
				return next;
			});
			setUnassignTarget(null);
			notification.open({
				message: 'Training unassigned',
				placement: 'topRight',
				duration: 3,
				className: 'lms-notification',
				closeIcon: <></>,
				icon: <Icon type="infoFilled" role="img" />,
				description: null
			});
		} catch {
			notification.open({
				message: 'Failed to unassign training',
				placement: 'topRight',
				duration: 3,
				className: 'lms-notification',
				closeIcon: <></>,
				icon: <Icon type="infoFilled" role="img" />,
				description: null
			});
		}
	};

	const handleExport = () => {
		console.log('Export triggered');
	};

	const handleNewAssignment = () => {
		history.push(`${url}/create-assignment`);
	};

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<HeaderRow
					title="Course assignments"
					subHeading="Create, manage and track training assignments"
				>
					{!showNoAssignments && (
						<div className={styles.header__actions}>
							<LMSButton
								variant="border"
								size="small"
								onClick={handleExport}
							>
								Export
							</LMSButton>
							<Button
								size="small"
								className="cta-prominent"
								type="primary"
								icon={<Icon type="plus" role="img" />}
								onClick={handleNewAssignment}
							>
								New assignment
							</Button>
						</div>
					)}
				</HeaderRow>
			</div>

			{showNoAssignments ? (
				<div className={styles.emptyState}>
					<div className={styles.emptyState__icon}>
						<Icon type="noAssignments" role="img" />
					</div>
					<div className={styles.emptyState__title}>
						No assignments found
					</div>
					<div className={styles.emptyState__description}>
						It looks like your organization doesn&apos;t have any
						assignments yet. Create an assignment to deliver
						training to your users.
					</div>
					<LMSButton
						type="primary"
						size="small"
						onClick={handleNewAssignment}
					>
						Create assignment
					</LMSButton>
				</div>
			) : (
				<>
					<div className={styles.filtersContainer}>
						<ToggleGroup
							value={activeTab}
							onChange={(value) => setActiveTab(value)}
							options={[
								{ key: '1', label: 'Assignments' },
								{ key: '2', label: 'Courses' }
							]}
						/>

						<Select
							value={selectedOrg}
							onChange={(val) => {
								setSelectedOrg(val);
								setFilterSearch('');
							}}
							style={{ width: 180, color: '#000' }}
							size="small"
							className={styles.orgSelect}
						>
							<Option value="all">All organization</Option>
							<Option value="roles">Roles</Option>
							<Option value="property">Property</Option>
							<Option value="individual">Individual</Option>
						</Select>
					</div>

					{selectedOrg !== 'all' && (
						<FilterSearch
							filterType={selectedOrg}
							value={filterSearch}
							onChange={setFilterSearch}
						/>
					)}

					{!showNoFilterMatches && (
						<div className={styles.listHeader}>
							<span className={styles.listHeader__label}>
								{activeTab === '1'
									? `Assignments by organization (
								${assignments.length})`
									: `	Courses by organization (${MOCK_COURSES.length})`}
							</span>
							{isSelectionActive && (
								<BulkActionsBar
									selectedCount={checkedIds.size}
									onSelectAll={handleSelectAll}
									onDeselectAll={handleDeselectAll}
									onReset={handleBulkReset}
									onUnassign={handleBulkUnassign}
								/>
							)}
						</div>
					)}

					{showNoFilterMatches ? (
						<div className={styles.emptyState}>
							<div className={styles.emptyState__icon}>
								<Icon type="filterNoAssignments" role="img" />
							</div>
							<div className={styles.emptyState__title}>
								No assignments match these filters
							</div>
							<div className={styles.emptyState__description}>
								Adjust the filters or search to see more
								results.
							</div>
						</div>
					) : (
						<>
							{activeTab === '1' && isLoadingAssignments && (
								<div className={styles.loading}>
									<Spin size="large" />
								</div>
							)}

							{activeTab === '1' && !isLoadingAssignments && (
								<div className={styles.list}>
									{assignments.map((assignment) => (
										<LearningPathCard
											key={assignment.id}
											learningPath={toLearningPathData(
												assignment
											)}
											checked={checkedIds.has(
												assignment.id
											)}
											onCheck={handleCheck}
											menuDisabled={isSelectionActive}
											menuOptions={[
												{
													key: 'view-details',
													label: 'View details'
												},
												{ key: 'edit', label: 'Edit' },
												...(assignment.status ===
												'Scheduled'
													? [
															{
																key:
																	'reset-completion',
																label:
																	'Reset completion'
															}
													  ]
													: []),
												{
													key: 'unassign',
													label: 'Unassign',
													danger: true
												}
											]}
											onMenuAction={handleMenuAction}
											onCourseClick={(course) =>
												setCourseDetailTarget(course)
											}
										/>
									))}
								</div>
							)}

							{activeTab === '2' && (
								<>
									<div className={styles.courseGrid}>
										{MOCK_COURSES.map((course) => (
											<CourseCard
												key={course.id}
												index={course.id}
												course={course}
												onClick={() =>
													setCourseDetailTarget(
														course
													)
												}
												showCheckbox
												checked={checkedCourseIds.has(
													course.id
												)}
												onCheck={(id, isChecked) =>
													setCheckedCourseIds(
														(prev) => {
															const next = new Set(
																prev
															);
															isChecked
																? next.add(id)
																: next.delete(
																		id
																  );
															return next;
														}
													)
												}
												showMenu
												onMenuAction={(id, action) => {
													const target = MOCK_COURSES.find(
														(c) => c.id === id
													);
													if (
														action ===
															'view-details' &&
														target
													) {
														setCourseDetailTarget(
															target
														);
													} else if (
														action ===
															'reset-completion' &&
														target
													) {
														setCourseResetTarget(
															target
														);
													} else if (
														action === 'unassign' &&
														target
													) {
														setCourseUnassignTarget(
															target
														);
													} else {
														console.log(
															'Course action:',
															action,
															id
														);
													}
												}}
											/>
										))}
									</div>
								</>
							)}
						</>
					)}
				</>
			)}

			<div className={styles.footer}>Powered by Venza</div>
			<StepperProvider>
				<UnassignTrainingModal
					visible={
						Boolean(unassignTarget) &&
						unassignTarget?.status !== 'Scheduled'
					}
					onSubmit={handleConfirmUnassign}
					onCancel={() => setUnassignTarget(null)}
				/>
				<UnassignScheduledModal
					visible={
						Boolean(unassignTarget) &&
						unassignTarget?.status === 'Scheduled'
					}
					assignmentId={Number(unassignTarget?.id ?? 0)}
					scopes={scopesForAssignment(unassignTarget?.id)}
					onCancel={() => setUnassignTarget(null)}
					onContinue={async (
						target: UnassignTarget,
						userIds?: string[]
					) => {
						if (!unassignTarget) return;
						if (target === 'specific') {
							try {
								await learningManagerStore.unassignAssignmentSourceUsers(
									Number(unassignTarget.id),
									userIds ?? []
								);
								setUnassignTarget(null);
								notify('Training unassigned');
							} catch {
								notify('Failed to unassign training');
							}
						} else {
							handleConfirmUnassign();
						}
					}}
				/>
				<ViewDetailsDrawer
					visible={Boolean(detailTarget)}
					detailTarget={detailTarget}
					onClose={() => setDetailTarget(null)}
				/>
				<CourseDetailsDrawer
					visible={Boolean(courseDetailTarget)}
					course={courseDetailTarget}
					onClose={() => setCourseDetailTarget(null)}
				/>
				<ResetCompletionModal
					visible={Boolean(resetTarget)}
					assignmentId={Number(resetTarget?.id ?? 0)}
					scopes={scopesForAssignment(resetTarget?.id)}
					onCancel={() => setResetTarget(null)}
					onContinue={async (
						target: ResetTarget,
						userIds?: string[]
					) => {
						if (!resetTarget) return;
						try {
							if (target === 'specific') {
								await learningManagerStore.resetAssignmentSourceUsers(
									Number(resetTarget.id),
									userIds ?? []
								);
							} else {
								await learningManagerStore.resetAssignmentSource(
									Number(resetTarget.id)
								);
							}
							setResetTarget(null);
							notify('Training reset');
						} catch {
							notify('Failed to reset training');
						}
					}}
				/>
				<BulkResetModal
					visible={showBulkReset}
					selectedCount={checkedIds.size}
					totalUserCount={assignments.length}
					onCancel={() => setShowBulkReset(false)}
					onConfirm={handleBulkResetConfirm}
				/>
				<UnassignTrainingModal
					visible={showBulkUnassign}
					assignmentCount={checkedIds.size}
					learnerCount={assignments
						.filter((a) => checkedIds.has(a.id))
						.reduce((sum, a) => sum + a.courseCount, 0)}
					onSubmit={handleBulkUnassignConfirm}
					onCancel={() => setShowBulkUnassign(false)}
				/>
				<BulkResetModal
					visible={Boolean(courseResetTarget)}
					entityType="course"
					selectedCount={2}
					totalUserCount={20}
					onCancel={() => setCourseResetTarget(null)}
					onConfirm={() => {
						setCourseResetTarget(null);
						notification.open({
							message: 'Training reset',
							placement: 'topRight',
							duration: 3,
							className: 'lms-notification',
							closeIcon: <></>,
							icon: <Icon type="infoFilled" role="img" />,
							description: null
						});
					}}
				/>
				<UnassignTrainingModal
					visible={Boolean(courseUnassignTarget)}
					courseCount={2}
					learnerCount={5}
					onSubmit={() => {
						setCourseUnassignTarget(null);
						notification.open({
							message: 'Training unassigned',
							placement: 'topRight',
							duration: 3,
							className: 'lms-notification',
							closeIcon: <></>,
							icon: <Icon type="infoFilled" role="img" />,
							description: null
						});
					}}
					onCancel={() => setCourseUnassignTarget(null)}
				/>
			</StepperProvider>
		</div>
	);
});

export default CourseAssignments;
